import { defineComponent as _defineComponent } from 'vue'
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { computed, ref } from 'vue';
import BuyBox from '@/components/buybox/BuyBox.vue';
import { MonetizationType, TitleListType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { excludePackages } from '@/constants/buybox-excluded-packages.constant';
import { isTitleInList } from '@/helpers/title-helper';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxBanner',
  props: {
    offerCount: null,
    offers: null,
    title: null,
    seasonNumber: { default: null },
    additionalContexts: null,
    isAVODActive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as { offerCount: number, offers: TitleOfferFragment[], title: TitleDetail, seasonNumber: number | null, additionalContexts: SnowplowContext[], isAVODActive?: boolean };

const { t } = useI18n();
const { country, language } = useLanguageStore();
const { isLoggedIn, isProUser } = useUserStore();

const showBuyBox = ref(false);



const showDetails = computed(() => {
	if (props.title.__typename !== 'Season') return null;
	return {
		id: props.title.id,
		title: props.title.content.title,
		objectId: props.title.show.objectId,
		objectType: props.title.show.objectType,
	};
});

function toggleBuyBox() {
	showBuyBox.value = !showBuyBox.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'dropdown_clicked', label: 'buybox', value: showBuyBox.value ? 1 : 0 },
		props.additionalContexts
	);
}

function scrollTo(id: string) {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
	TrackingHelper.trackEvent('userinteraction', {
		action: 'jumplink_clicked',
		label: id.replaceAll('-', '_'),
	});
}

const showTheatreAndProviderText = computed(() => {
	return ['en', 'fr', 'de', 'es', 'tr', 'pt', 'it'].includes(language.value);
});

const hasOnlyCinemaOffers = computed(() => {
	return props.offers.every(offer => offer.monetizationType === MonetizationType.Cinema);
});

const cinemaOffersCount = computed(() => {
	return props.offers.filter(offer => offer.monetizationType === MonetizationType.Cinema).length;
});

const nonCinemaOffersCount = computed(() => {
	return providerCount.value - cinemaOffersCount.value;
});

const offerCountText = computed(() => {
	if (hasOnlyCinemaOffers.value) return t('WEBAPP_CURRENTLY_IN_THEATERS');

	if (cinemaOffersCount.value > 0 && showTheatreAndProviderText.value)
		return nonCinemaOffersCount.value === 1
			? t('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICE')
			: t('WEBAPP_IN_THEATERS_AND_ON_STREAMING_SERVICES', {
					count: nonCinemaOffersCount.value,
			  });

	return providerCount.value === 1
		? t('WEBAPP_AVAIL_ON_STREAMING_SERVICE')
		: t('WEBAPP_AVAIL_ON_STREAMING_SERVICES', {
				count: providerCount.value,
		  });
});

const excludedPackages = computed(() => {
	const entityId = props.title.__typename === 'Season' ? props.title.show.id : props.title.id;

	return excludePackages(isLoggedIn.value, entityId, country.value, isProUser.value);
});

const providerCount = computed(() => {
	// START AVOD
	// Only show One Plex offer at a time.
	// + Always filtering out Justwatch-plex Provider as it's only used by partners.
	const AVODTechnicalName = props.isAVODActive
		? ['plex', 'justwatchplexchannel']
		: ['plexplayer', 'justwatchplexchannel'];

	return [...new Map(props.offers.map(offer => [offer.package.packageId, offer])).values()].filter(
		offer =>
			// Filtering out TVOD providers we don't have a partnership with
			!excludedPackages.value.includes(offer.package.shortName) &&
			// Only show one plex offer
			!AVODTechnicalName.includes(offer.package.technicalName)
	).length;
});

return { __sfc: true,t, country, language, isLoggedIn, isProUser, showBuyBox, props, showDetails, toggleBuyBox, scrollTo, showTheatreAndProviderText, hasOnlyCinemaOffers, cinemaOffersCount, nonCinemaOffersCount, offerCountText, excludedPackages, providerCount, BuyBox, TitleListType, isTitleInList, faChevronUp, faChevronDown }
}

})